import {useCallback, useEffect, useState} from "react";
import "./TeamPage.scss";
import useQueryString from "../../../../hooks/useQueryString";
import Storage from "../../../../util/Storage";
import TeamBanner from "../../TeamBanner";
import UserCard from "../../user/UserCard";
import {AddOnDtoTypeEnum, MemberDto,} from "../../../../generated/account-client";
import {useRecoilState} from "recoil";
import AccountsApi from "../../../../api/AccountsApi";
import {memberState} from "../../../../state/atoms";
import UsersApi from "../../../../api/UsersApi";
import {success} from "../../../../service/MessageService";
import {handleServerError} from "../../../../util/ErrorHandler";
import AvailableSeatCard from "../../user/AvailableSeatCard";
import {useNavigate} from "react-router-dom";
import AccountMembersApi from "../../../../api/AccountMembersApi";
import {MemberDtoPlanEnum} from "../../../../generated/dashboard-client";
import {Skeleton} from "antd";
import {UserOutlined} from "@ant-design/icons";
import TeamPermissionsTable from "../../TeamPermissionsTable";

export function TeamPage() {
  const navigate = useNavigate();
  const [tkn] = useQueryString<string>("tkn", Storage.getToken());
  const [member] = useRecoilState(memberState);

  const [loading, setLoading] = useState(true);
  const [userLocationsLoading, setUserLocationsLoading] = useState(false);
  const [users, setUsers] = useState([] as MemberDto[]);
  const [seats, setSeats] = useState(0);
  const [nextBillingDate] = useState("");

  const [isFreeAccount, setIsFreeAccount] = useState(false);

  useEffect(() => {
    if (tkn) {
      Storage.setToken(tkn);
    }
    setLoading(false);
  }, [navigate, tkn]);

  const fetchUserDetails = useCallback(() => {
    if (!member.id || !member.plan) return;

    setLoading(true);
    AccountsApi.getUsersDetails(member.id)
      .then((resp) => {
        setLoading(false);

        if (member.plan === MemberDtoPlanEnum.Free) setIsFreeAccount(true);
        setUsers(resp.data.users || []);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [member.id, member.plan]);

  const fetchAvailableSeats = useCallback(() => {
    if (!member.teamId) return;

    AccountsApi.getTeamAvailableSeats(member.teamId)
      .then((resp) => {
        resp.data ? setSeats(resp.data) : setSeats(0);
      })
      .catch((e) => {
        handleServerError(e);
      });
  }, [member.teamId]);

  useEffect(fetchAvailableSeats, [fetchAvailableSeats]);
  useEffect(fetchUserDetails, [fetchUserDetails]);

  function addNewUser() {
    if (isFreeAccount) window.MHM.ProModal.upgradeWithUsers({
      memberId: member.id,
      onSuccess: () => window.location.reload()
    });
    else window.MHM.ProModal.addUser({
      memberId: member.id, onSuccess: () => {
        fetchUserDetails();
        fetchAvailableSeats();
      }
    });
  }

  function resend(member: MemberDto) {
    UsersApi.sendInvitationEmail(member.id || "")
      .then(() => {
        success("Invitation sent!");
      })
      .catch();
  }

  function remove(user: MemberDto) {
    if (!member.id) return;

    setLoading(true);
    AccountsApi.removeSubscriptionAddon(member.id, AddOnDtoTypeEnum.User)
      .then(() => {
        UsersApi.removeUser(user.id!)
          .then(() => {
            success("Invitation revoked!");
            setUsers(users.filter((it) => it.id !== user.id));
            setSeats(seats > 0 ? seats + 1 : 0);
            setLoading(false);
          })
          .catch((e) => {
            handleServerError(e);
            setLoading(false);
          });
      })
      .catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
  }

  function revoke(user: MemberDto) {
    console.log("on revoked");
    if (!member.id) return;

    setLoading(true);
    AccountsApi.removeSubscriptionAddon(member.id, AddOnDtoTypeEnum.User)
      .then(() => {
        UsersApi.revokeInvitation(user.id!)
          .then(() => {
            success("Invitation revoked!");
            setUsers(users.filter((it) => it.id !== user.id));
            setSeats(seats > 0 ? seats + 1 : 0);
            setLoading(false);
          })
          .catch((e) => {
            handleServerError(e);
            setLoading(false);
          });
      })
      .catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
  }

  function changeLocationAccess(member: MemberDto, locations: Set<string>, callback?: () => void) {
    console.log("locations", locations);
    setUserLocationsLoading(true);
    AccountMembersApi.updateLocationsForMember(
      member.id || "",
      Array.from(locations)
    )
      .then((response) => {
        const resp = response.data;
        users[users.indexOf(member)].locations = resp.locations;
        setUsers(users);
        setUserLocationsLoading(false);
        if (callback) {
          callback();
        }
      })
      .catch((e) => {
        handleServerError(e);
        setUserLocationsLoading(false);
        if (callback) {
          callback();
        }
      });
  }

  function computeAvailableSeats() {
    const section = [];
    let availableSeats = seats;
    while (availableSeats) {
      section.push(<AvailableSeatCard date={nextBillingDate}/>);
      availableSeats -= 1;
    }

    return section;
  }

  return (
    <div className={"content team-page"}>
      {loading && <Skeleton/>}

      {!loading && (
        <>
          <TeamBanner/>

          <TeamPermissionsTable
            owner={member}
            users={users}
            loading={loading}
            onRevoke={revoke}
            onResend={resend}
            onRemove={remove}
            onChangeLocationAccess={changeLocationAccess}/>

          <section className={"team"}>
            <div>
              <div className={"add-new"} onClick={addNewUser}>
                <UserOutlined/>
                <h4>Add User</h4>
              </div>
              {seats > 0 && computeAvailableSeats()}
              {users.map((it) => (
                <UserCard
                  key={it.id}
                  member={it}
                  onRevoke={revoke}
                  onResend={resend}
                  onRemove={remove}
                  onChangeLocationAccess={changeLocationAccess}
                  loading={userLocationsLoading}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
